import { deviceName, keyName, pinName, pinNode, placedOrder, type Analysis } from './analyze';
import { catOf, type PartLookup } from './catalog';
import { loc } from './geometry';
import { LIB } from './library';
import type { Placed, Project } from './types';
import { t as $t, tk } from './i18n';


/* PLC channels: address parsing, I/O list, readdressing and TIA Portal tag export. */

export type PlcKind = 'DI' | 'DQ' | 'AI' | 'AQ';
export const PLC_KIND: Record<string, PlcKind> = { plcin: 'DI', plcout: 'DQ', plcai: 'AI', plcao: 'AQ' };
export const PLC_KIND_NAME: Record<PlcKind, string> = { DI: tk("Dijital giriş"), DQ: tk("Dijital çıkış"), AI: tk("Analog giriş"), AQ: tk("Analog çıkış") };

export interface PlcAddress {
  area: 'I' | 'Q';
  word: boolean;
  byte: number;
  bit: number;
}

/**
 * Parses Siemens-style addresses: I0.0, Q1.7, IW64, QW66, with optional "%" and the
 * German mnemonics (E = input, A = output), and the IEC 61131-3 bit form of CODESYS (%IX0.0).
 * Returns null for anything else.
 */
export function parsePlcAddress(a: string): PlcAddress | null {
  const m = /^%?([IQEA])([WX]?)\s*(\d{1,5})(?:\.([0-7]))?$/i.exec((a || '').trim());
  if (!m) return null;
  const area = /[IE]/i.test(m[1]) ? 'I' : 'Q', word = /w/i.test(m[2]);
  if (word === (m[4] !== undefined)) return null; // bit address needs ".bit", word address must not have one
  return { area, word, byte: +m[3], bit: m[4] === undefined ? 0 : +m[4] };
}

export const formatPlcAddress = (p: PlcAddress) => (p.word ? `${p.area}W${p.byte}` : `${p.area}${p.byte}.${p.bit}`);
const addrKey = (p: PlcAddress) => `${p.area}${p.word ? 'W' : ''}${p.byte}.${p.word ? 0 : p.bit}`;
const addrOrder = (p: PlcAddress | null) => (p ? (p.area === 'I' ? 0 : 1e7) + p.byte * 8 + p.bit : 2e7);

export interface PlcChannel {
  it: Placed;
  key: string;
  kind: PlcKind;
  addr: string;
  parsed: PlcAddress | null;
}

export function plcChannels(A: Analysis): PlcChannel[] {
  return A.all
    .filter((it) => PLC_KIND[it.s.type])
    .map((it) => {
      const addr = it.s.props.addr || '';
      return { it, key: A.devKey.get(it.s.id) ?? '', kind: PLC_KIND[it.s.type], addr, parsed: parsePlcAddress(addr) };
    });
}

/** What a PLC channel is wired to (other device pins on the same net). */
function wiredTo(A: Analysis, it: Placed): string {
  const r = A.uf.find(pinNode(it.s, 0)), out: string[] = [];
  for (const x of A.all) {
    const role = LIB[x.s.type].role;
    if (x.s.id === it.s.id || role === 'brk' || role === 'text' || role === 'cable') continue;
    LIB[x.s.type].pins.forEach((_, j) => {
      if (A.uf.find(pinNode(x.s, j)) === r) out.push(pinName(A, x.s, j));
    });
  }
  return out.join(', ');
}

export interface PlcIssue {
  sev: 'err' | 'warn';
  msg: string;
  it: Placed;
}

export function plcIssues(A: Analysis, parts: PartLookup = catOf): PlcIssue[] {
  const out: PlcIssue[] = [];
  const ch = plcChannels(A);
  const seen = new Map<string, PlcChannel>();
  for (const c of ch) {
    const nm = deviceName(A, c.it.s);
    // channels of a generator controller are named by its own terminals (DI1, DO3 …), not PLC addresses
    if (A.mains.get(A.devKey.get(c.it.s.id) ?? '')?.[0]?.s.type === 'gctl') continue;
    if (!c.parsed) {
      out.push({ sev: 'warn', msg: $t("{0}: \"{1}\" tanınan bir PLC adresi değil (ör. I0.0, Q1.3, IW64)", nm, c.addr || $t("(boş)")), it: c.it });
      continue;
    }
    const wantIn = c.kind === 'DI' || c.kind === 'AI', wantWord = c.kind === 'AI' || c.kind === 'AQ';
    if ((c.parsed.area === 'I') !== wantIn || c.parsed.word !== wantWord)
      out.push({ sev: 'err', msg: $t("{0}: {1} adresi bir {2} kanalına uymuyor", nm, c.addr, $t(PLC_KIND_NAME[c.kind]).toLowerCase()), it: c.it });
    const k = addrKey(c.parsed);
    const prev = seen.get(k);
    if (prev) out.push({ sev: 'err', msg: $t("{0}: {1} adresi zaten kullanılıyor (/{2})", nm, c.addr, loc(prev.it)), it: c.it });
    else seen.set(k, c);
  }
  // channel capacity per card (part "ch")
  const byCard = new Map<string, PlcChannel[]>();
  for (const c of ch) (byCard.get(c.key) ?? byCard.set(c.key, []).get(c.key)!).push(c);
  for (const [key, list] of byCard) {
    const pn = list.find((c) => c.it.s.part)?.it.s.part;
    const cap = pn ? parts(pn)?.ch : undefined;
    if (cap != null && list.length > cap)
      out.push({ sev: 'err', msg: $t("{0}: {1} kanal kullanılmış, {2} yalnızca {3} kanal sağlıyor", keyName(A, key), list.length, pn, cap), it: list[cap].it });
  }
  return out;
}

export interface PlcRow {
  it: Placed;
  cells: string[];
}

/** A PLC tag name from free text: letters, digits and "_" only (TIA Portal and CODESYS accept it). */
export const plcTagName = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ı/g, 'i').replace(/İ/g, 'I')
    .replace(/[^A-Za-z0-9_ ]+/g, ' ').trim().replace(/\s+/g, '_').slice(0, 100);

/** I/O list sorted by card, then address. */
export function plcList(A: Analysis): PlcRow[] {
  return plcChannels(A)
    .sort((a, b) => keyName(A, a.key).localeCompare(keyName(A, b.key), 'tr', { numeric: true }) || addrOrder(a.parsed) - addrOrder(b.parsed) || placedOrder(a.it, b.it))
    .map((c) => ({
      it: c.it,
      cells: [keyName(A, c.key), c.parsed ? formatPlcAddress(c.parsed) : c.addr, c.kind, c.it.s.props.sym ?? '', c.it.s.desc, wiredTo(A, c.it), c.it.s.part, '/' + loc(c.it)],
    }));
}

export interface ReaddressStart {
  DI: string;
  DQ: string;
  AI: string;
  AQ: string;
}
export const DEFAULT_START: ReaddressStart = { DI: 'I0.0', DQ: 'Q0.0', AI: 'IW64', AQ: 'QW64' };

/**
 * Renumbers the channels of one PLC card (or all cards when key is '') in drawing
 * order (page, column). Bits advance 0–7 then the next byte; words advance by 2.
 * Returns how many addresses changed.
 */
export function readdressPlc(A: Analysis, key: string, start: ReaddressStart = DEFAULT_START): number {
  const next: Record<PlcKind, PlcAddress> = {} as any;
  for (const k of ['DI', 'DQ', 'AI', 'AQ'] as PlcKind[]) {
    const p = parsePlcAddress(start[k]);
    if (!p) throw new Error($t("Başlangıç adresi geçersiz: {0}", start[k]));
    next[k] = { ...p };
  }
  let changed = 0;
  const list = plcChannels(A).filter((c) => !key || c.key === key).sort((a, b) => placedOrder(a.it, b.it));
  for (const c of list) {
    const p = next[c.kind];
    const addr = formatPlcAddress(p);
    if (c.it.s.props.addr !== addr) {
      c.it.s.props.addr = addr;
      changed++;
    }
    if (p.word) p.byte += 2;
    else if (++p.bit > 7) {
      p.bit = 0;
      p.byte++;
    }
  }
  return changed;
}

export interface PlcTag {
  c: PlcChannel;
  /** unique tag name (letters, digits, "_") */
  name: string;
  type: 'Bool' | 'Int';
  comment: string;
}

/**
 * The PLC tags of the channels in I/O list order: the symbol name given to a channel (e.g. read from an
 * assignment list), else one made from its description, else card and address; made unique.
 */
export function plcTags(A: Analysis): PlcTag[] {
  const used = new Set<string>();
  const byId = new Map(plcChannels(A).map((c) => [c.it.s.id, c]));
  return plcList(A).map((r) => {
    const c = byId.get(r.it.s.id)!;
    const base = plcTagName(c.it.s.props.sym || c.it.s.desc || `${keyName(A, c.key).replace(/^[-=+]/, '')}_${c.addr}`) || 'Tag';
    let name = base, n = 2;
    while (used.has(name.toLowerCase())) name = `${base}_${n++}`;
    used.add(name.toLowerCase());
    const comment = [c.it.s.desc, r.cells[5] ? `→ ${r.cells[5]}` : '', `/${loc(c.it)}`].filter(Boolean).join(' ').slice(0, 500);
    return { c, name, type: c.kind === 'AI' || c.kind === 'AQ' ? 'Int' : 'Bool', comment };
  });
}

/**
 * TIA Portal PLC tag table (import via "PLC tags → Import"): the column layout
 * TIA Portal itself exports.
 */
export function tiaTagTable(pr: Project, A: Analysis): string[][] {
  const head = ['Name', 'Path', 'Data Type', 'Logical Address', 'Comment', 'Hmi Visible', 'Hmi Accessible', 'Hmi Writeable', 'Typeobject ID', 'Version ID'];
  const rows = plcTags(A).map(({ c, name, type, comment }) =>
    [name, 'Default tag table', type, '%' + (c.parsed ? formatPlcAddress(c.parsed) : c.addr), comment, 'True', 'True', c.kind === 'DQ' || c.kind === 'AQ' ? 'True' : 'False', '', '']);
  void pr;
  return [head, ...rows];
}
